import { useSelector } from 'react-redux';
import {
  useAddExchangeConnectionMutation,
  useCheckExchangeConnectionMutation,
  useGetUserExchangesQuery,
  useRemoveExchangeConnectionMutation,
} from '../../../store/services/api';
import { SubscriptionSummary } from '../../../types/subscription';
import { selectUserId } from '../../../store/slices/user';
import { useEffect, useMemo, useState } from 'react';
import { Exchange } from 'grts-utils/src/types/Exchange';
import DropdownComponent from '../../../components/Form/Dropdown';
import InputField from '../../../components/Form/InputField';
import { colors } from '../../../colors';
import { Validations } from '../../../components/Form';
import { selectSubscriptionSummaries } from '../../../store/slices/subscription';

interface ExchangeProps {
  userId: number;
  exchanges: Exchange[] | undefined;
  type?: 'EDIT' | 'ADD';
}

/** Fetch current active exchange connections and let the user input new key and secret */
const EditExchangeConnections = (props: ExchangeProps) => {
  const [testExchangeConnection] = useCheckExchangeConnectionMutation();
  const [addExchangeConnections] = useAddExchangeConnectionMutation();

  const [activeExchange, setActiveExchange] = useState(props.exchanges?.[0]);

  const title =
    props.type === 'ADD'
      ? 'Add Exchange Connection'
      : 'Edit Exchange Connection';
  const buttonText =
    props.type === 'ADD' ? 'Add Connection' : 'Edit Connection';
  const successText =
    props.type === 'ADD'
      ? ' Exchange Connection successfully added!'
      : ' Exchange Connection successfully edited!';

  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');

  const [testConnectionResult, setTestConnectionResult] = useState<
    boolean | undefined
  >();
  const [editConnectionResult, setEditConnectionResult] = useState<
    boolean | undefined
  >();

  const isConnectionValid = testConnectionResult;

  const handleExchangeChange = (exchange: Exchange) => {
    setKey('');
    setSecret('');
    setPassphrase('');
    setTestConnectionResult(undefined);
    setActiveExchange(exchange);
  };

  const checkConnection = async () => {
    if (!activeExchange) {
      return;
    }

    const result = await testExchangeConnection({
      exchange: activeExchange,
      key,
      secret,
      passphrase,
    });

    setTestConnectionResult(result.data?.success);
  };

  const editConnection = async () => {
    if (!activeExchange) {
      return;
    }

    const result = await addExchangeConnections({
      userId: props.userId,
      exchange: activeExchange,
      key,
      secret,
      passphrase,
    });

    setEditConnectionResult(result.data?.success);
  };

  const isTestConnectionButtonDisabled =
    !key || !secret || (activeExchange === 'KUCOIN' && !passphrase);
  const isEditConnectionButtonDisabled =
    isTestConnectionButtonDisabled || !isConnectionValid;

  if (!props.exchanges?.length) {
    return null;
  }

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        {title}
      </p>
      <div style={{ marginTop: 16 }}>
        <DropdownComponent
          config={{
            label: 'Exchanges',
            value: activeExchange,
            handleValueChange: handleExchangeChange,
            options: props.exchanges || [],
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          marginBottom: 16,
          marginTop: 16,
        }}
      >
        <InputField
          config={{
            label: 'Key',
            value: key,
            handleValueChange: setKey,
            shouldFit: true,
            error:
              isConnectionValid === false
                ? Validations.ConnectionRefused
                : undefined,
          }}
        />
        <InputField
          config={{
            label: 'Secret',
            value: secret,
            handleValueChange: setSecret,
            shouldFit: true,
            error:
              isConnectionValid === false
                ? Validations.ConnectionRefused
                : undefined,
          }}
        />
        {activeExchange === 'KUCOIN' && (
          <InputField
            config={{
              label: 'Passphrase',
              value: passphrase,
              handleValueChange: setPassphrase,
              shouldFit: true,
              error:
                isConnectionValid === false
                  ? Validations.ConnectionRefused
                  : undefined,
            }}
          />
        )}
      </div>
      <div
        style={{
          display: 'flex',
          columnGap: 16,
          marginTop: 16,
        }}
      >
        <button
          style={{
            backgroundColor: isTestConnectionButtonDisabled
              ? colors.gray
              : colors.orange,
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 8,
            paddingBottom: 8,
            color: colors.white,
            borderRadius: 4,
            fontWeight: 800,
            border: 0,
            cursor: isTestConnectionButtonDisabled ? 'default' : 'pointer',
          }}
          disabled={isTestConnectionButtonDisabled}
          onClick={checkConnection}
        >
          Test Connection
        </button>
        <button
          style={{
            backgroundColor:
              typeof testConnectionResult === 'undefined'
                ? colors.gray
                : testConnectionResult
                ? colors.green
                : colors.red,
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 8,
            paddingBottom: 8,
            color: colors.white,
            borderRadius: 4,
            fontWeight: 800,
            border: 0,
            cursor: isEditConnectionButtonDisabled ? 'default' : 'pointer',
          }}
          disabled={isEditConnectionButtonDisabled}
          onClick={editConnection}
        >
          {buttonText}
        </button>
      </div>
      {editConnectionResult && (
        <p style={{ color: colors.green, margin: 0, padding: 0, marginTop: 8 }}>
          {successText}
        </p>
      )}
    </div>
  );
};

// Fetch subscriptions and let the user remove it, show effected subscriptions
// and pause all those subscriptions, don't let the user resume any of these subsctiptions
// without exchange connection being in place and having enough balance

const RemoveExchangeConnections = (props: ExchangeProps) => {
  const [removeExchangeConnections] = useRemoveExchangeConnectionMutation();
  const subscriptions = useSelector(selectSubscriptionSummaries);
  const [activeExchange, setActiveExchange] = useState(props.exchanges?.[0]);

  const [removeConnectionResult, setRemoveConnectionResult] = useState<
    boolean | undefined
  >();

  const affectedSubscriptions = useMemo(() => {
    return subscriptions.filter((s) => s.exchange === activeExchange);
  }, [activeExchange, subscriptions]);

  const removeConnection = async () => {
    if (!activeExchange) {
      return;
    }

    const result = await removeExchangeConnections({
      userId: props.userId,
      exchange: activeExchange,
    });

    setRemoveConnectionResult(result.data?.success);
  };

  return (
    <div
      style={{
        paddingLeft: 16,
        paddingRight: 16,
        marginTop: -5,
        paddingBottom: 32,
        borderBottom: '0.5px solid rgba(80, 80, 80, 0.3)',
      }}
    >
      <p
        style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 20,
          marginBottom: 5,
        }}
      >
        Remove Exchange Connections
      </p>
      <div style={{ marginTop: 16 }}>
        <DropdownComponent
          config={{
            label: 'Exchanges',
            value: activeExchange,
            handleValueChange: setActiveExchange,
            options: props.exchanges || [],
          }}
        />
      </div>
      {!!affectedSubscriptions?.length && (
        <>
          <p style={{ color: colors.orange }}>
            By removing an exchange connection, all affected bots will be paused
            and balances will remain untouched. These subscriptions can only be
            resumed when a new exchange connection of the same exchange is
            created. Balances of these subscriptions can still be adjusted
            and/or be removed.{' '}
          </p>
          <p style={{ color: colors.white, fontWeight: 'bold', fontSize: 18 }}>
            Affected Subsriptions:
          </p>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}
          >
            {affectedSubscriptions.map((s) => (
              <div key={s.id} style={{ color: colors.white, marginBottom: 4 }}>
                <p style={{ padding: 0, margin: 0 }}>
                  {s.baseAsset}-{s.quoteAsset}
                </p>
                {!!s.assetBalance && (
                  <p
                    style={{
                      fontSize: 14,
                      color: colors.gray,
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {s.assetBalance} {s.baseAsset}
                  </p>
                )}
                {!!s.stableBalance && (
                  <p
                    style={{
                      fontSize: 14,
                      color: colors.gray,
                      padding: 0,
                      margin: 0,
                    }}
                  >
                    {s.stableBalance} {s.quoteAsset}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      <button
        style={{
          backgroundColor: colors.red,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 8,
          paddingBottom: 8,
          color: colors.white,
          borderRadius: 4,
          fontWeight: 800,
          border: 0,
          cursor: 'pointer',
          marginTop: 16,
        }}
        onClick={removeConnection}
      >
        Remove Connection
      </button>
      {typeof removeConnectionResult !== 'undefined' && (
        <p
          style={{ color: removeConnectionResult ? colors.green : colors.red }}
        >
          {!removeConnectionResult
            ? 'Something went wrong removing the exchange connection'
            : 'Your exchange connection has been successfully removed and affected subscriptions were paused.'}
        </p>
      )}
    </div>
  );
};

const AccountExchanges = () => {
  // Fetch exchanges of user + all exchanges
  const userId = useSelector(selectUserId);
  const { data } = useGetUserExchangesQuery(userId);

  const newExchanges = useMemo(() => {
    return data?.allExchanges?.filter(
      (e) => !data?.enabledExchanges?.includes(e),
    );
  }, [data]);

  if (!userId || !data) {
    return null;
  }

  return (
    <div style={{ padding: 28, paddingTop: 16 }}>
      <EditExchangeConnections
        userId={userId}
        exchanges={data?.enabledExchanges}
        type="EDIT"
      />
      <EditExchangeConnections
        userId={userId}
        exchanges={newExchanges}
        type="ADD"
      />
      <RemoveExchangeConnections
        userId={userId}
        exchanges={data?.enabledExchanges}
      />
    </div>
  );
};

export default AccountExchanges;
